import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { useState } from "react";
import {
  CanvasToolbarButton,
  CanvasToolbarGroup,
  CanvasToolbarShell,
} from "../../components/toolbar";
import { canvasToolbarButtonVariants } from "../../components/toolbar/canvasToolbarStyles";
import type { CanvasColor } from "./CanvasColorPalettes";
import {
  CANVAS_NO_FILL_VALUE,
  DEFAULT_LABEL_COLOR,
} from "./CanvasColorPalettes";
import { markOf } from "./canvasColorRoles";
import {
  CanvasToolbarColorSwatch,
  type CanvasSwatchRole,
} from "./CanvasToolbarGraphics";

function isNoFillSwatch(color: CanvasColor): boolean {
  return color.value === CANVAS_NO_FILL_VALUE;
}

interface CanvasColorPickerButtonProps {
  colors: CanvasColor[];
  /** What the picker sets; the swatch draws it (D44). */
  role: CanvasSwatchRole;
  /** Currently selected color (matches CanvasColor.value or .swatchHex). */
  activeValue: string;
  tooltip?: string;
  onColorChange: (value: string) => void;
  /**
   * When set, the transparent swatch toggles fill independently of `activeValue`
   * (shapes/sections: color drives border; no-fill only hides the fill).
   */
  noFill?: boolean;
  onNoFillChange?: (noFill: boolean) => void;
  onOpenChange?: (open: boolean) => void;
  /** Optional header rendered above the color swatches in the popover */
  renderPopoverHeader?: () => React.ReactNode;
  /** Which side the color picker popover opens toward. Defaults to "top". */
  popoverSide?: "top" | "bottom" | "left" | "right";
  /** Distance in px between the trigger and the popover. Defaults to 8. */
  popoverSideOffset?: number;
  /** Optional class on the swatch trigger (primary flyout menus use h-9 w-9). */
  triggerClassName?: string;
  /**
   * Which field to emit via onColorChange when a swatch is clicked.
   * "value"    — emits color.value (default; used by shapes which store the tint fill).
   * "swatchHex"— emits color.swatchHex (used by connectors/edges which store the saturated stroke).
   */
  emitField?: "value" | "swatchHex";
}

/**
 * What one palette entry draws for a role (experiment 19 `swatchFor`): the
 * tint for a fill, the tint with its dark stop for a Section, the mark for a
 * line or text — the entry's own value, or its swatch colour for Default.
 */
export function swatchFor(
  role: CanvasSwatchRole,
  color: CanvasColor,
): { fill?: string; stroke?: string; mark?: string } {
  if (role === "mark" || role === "text") {
    return {
      mark: color.value === DEFAULT_LABEL_COLOR ? color.swatchHex : color.value,
    };
  }
  if (isNoFillSwatch(color)) return { fill: color.value };
  return {
    fill: color.swatchFill ?? color.value,
    stroke: color.swatchStroke ?? color.swatchHex,
  };
}

/** A stored value as the row stores it: a mark role reads a dark stop as its mark. */
function roleValue(role: CanvasSwatchRole, value: string): string {
  return role === "mark" || role === "text" ? markOf(value) : value;
}

/**
 * Reusable color picker swatch button + popover.
 * Color row matches specs/canvas-toolbars ColorRow (Type-1 toolbar submenu).
 */
export function CanvasColorPickerButton({
  colors,
  role,
  activeValue,
  tooltip = "Change color",
  onColorChange,
  noFill = false,
  onNoFillChange,
  onOpenChange,
  renderPopoverHeader,
  popoverSide = "top",
  popoverSideOffset = 8,
  triggerClassName,
  emitField = "value",
}: CanvasColorPickerButtonProps) {
  // Match on value first (shapes store the tint), then swatchHex (connectors/edges
  // store the saturated color directly). Falls back to null when no match.
  const active = roleValue(role, activeValue);
  const current =
    colors.find((c) => c.value === active) ??
    colors.find((c) => c.swatchHex === activeValue) ??
    null;
  const triggerSwatch = current
    ? swatchFor(role, current)
    : { fill: activeValue, stroke: activeValue, mark: activeValue };
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const colorRow = (
    <CanvasToolbarShell
      aria-label="Color"
      className={cn(
        "max-w-none",
        renderPopoverHeader && "border-0 p-0 shadow-none",
      )}
    >
      <CanvasToolbarGroup>
        {colors.map((color) => {
          const swatch = swatchFor(role, color);

          if (isNoFillSwatch(color) && onNoFillChange) {
            return (
              <CanvasToolbarButton
                key={color.value}
                label={color.name}
                active={noFill}
                onClick={() => {
                  onNoFillChange(!noFill);
                }}
              >
                <CanvasToolbarColorSwatch role={role} {...swatch} />
              </CanvasToolbarButton>
            );
          }

          const isActive =
            active === color.value || activeValue === color.swatchHex;

          return (
            <CanvasToolbarButton
              key={color.value}
              label={color.name}
              active={isActive}
              onClick={() => {
                onColorChange(
                  emitField === "swatchHex" ? color.swatchHex : color.value,
                );
                setIsOpen(false);
                onOpenChange?.(false);
              }}
            >
              <CanvasToolbarColorSwatch role={role} {...swatch} />
            </CanvasToolbarButton>
          );
        })}
      </CanvasToolbarGroup>
    </CanvasToolbarShell>
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={tooltip}
              className={cn(canvasToolbarButtonVariants(), triggerClassName)}
              onClick={(e) => e.stopPropagation()}
            >
              <CanvasToolbarColorSwatch role={role} {...triggerSwatch} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        align="center"
        side={popoverSide}
        sideOffset={popoverSideOffset}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {renderPopoverHeader ? (
          <div className="flex flex-col gap-2 rounded-lg border border-n-10 bg-n-00 p-2 shadow-elev-5">
            {renderPopoverHeader()}
            {colorRow}
          </div>
        ) : (
          colorRow
        )}
      </PopoverContent>
    </Popover>
  );
}
